import { API_URL } from "env";

import { mixLogOut } from '../services/mixin';

export interface ChannelGroupConnection {
    id: number;
    display_name: string;
    channel_type_code: string;
    avatar_url?: string;
    external_id?: string;
    pivot?: {
        channel_group_id: number;
        channel_connection_id?: number;
    };
}

export interface ChannelGroupItem {
    id: number;
    name: string;
    created_by_id: number;
    created_at: string;
    updated_at: string;
    connections?: ChannelGroupConnection[];
}

export interface ChannelGroupListResponse {
    status: boolean;
    items: ChannelGroupItem[];
}

export async function getChannelGroups(params?: {
    page?: number;
    limit?: number;
}): Promise<ChannelGroupListResponse> {
    try {
        const token = localStorage.getItem('token') || '';
        const query = new URLSearchParams();
        if (params?.page) query.append('page', String(params.page));
        if (params?.limit) query.append('limit', String(params.limit));
        const queryString = query.toString();

        const response = await fetch(`${API_URL}/api/v1/be/channel-groups${queryString ? `?${queryString}` : ''}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const json = await response.json();
        const raw = json?.data;
        // API có thể trả mảng trực tiếp hoặc dạng phân trang { items: [...] }
        const items: ChannelGroupItem[] = Array.isArray(raw) ? raw : (raw?.items || []);
        return { status: !!json?.status, items };
    } catch (error) {
        console.error('Failed to fetch channel groups:', error);
        return { status: false, items: [] };
    }
}

export interface ChannelGroupDetailResponse {
    status: boolean;
    data: ChannelGroupItem | null;
}

export async function getChannelGroupById(id: number): Promise<ChannelGroupDetailResponse> {
    try {
        const token = localStorage.getItem('token') || '';
        const response = await fetch(`${API_URL}/api/v1/be/channel-groups/${id}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json() as ChannelGroupDetailResponse;
    } catch (error) {
        console.error('Failed to fetch channel group detail:', error);
        return {
            status: false,
            data: null,
        };
    }
}

// Lưu ý: channel_connection_ids dùng cơ chế đồng bộ đè (sync) — luôn gửi
// toàn bộ danh sách ID kênh của nhóm. Không truyền key này nếu chỉ đổi tên.
export async function updateChannelGroup(id: number, payload: {
    name?: string;
    channel_connection_ids?: number[];
}): Promise<ChannelGroupDetailResponse> {
    try {
        const token = localStorage.getItem('token') || '';
        const response = await fetch(`${API_URL}/api/v1/be/channel-groups/${id}`, {
            method: 'PUT',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json() as ChannelGroupDetailResponse;
    } catch (error) {
        console.error('Failed to update channel group:', error);
        return {
            status: false,
            data: null,
        };
    }
}
